import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { WarningOutlined } from "@ant-design/icons";
import AuthPageShell from "../../shared/AuthPageShell";

export const INTUIT_SECURITY_URL =
  "https://accounts.intuit.com/app/account-manager/security";

export default function IntuitUnverified() {
  const { logout, user } = useAuth0();

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const userEmail = user?.email || "";

  return (
    <AuthPageShell
      title="Email Verification Required"
      subtitle="Your Intuit account email address is not yet verified."
      showLogout={true}
      heroTitle={
        <>
          QuickBooks &amp; Intuit <br />
          <span className="relative inline-block">
            Email Verification
          </span>{" "}
          Required
        </>
      }
    >
      <div className="w-full space-y-5">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3 text-amber-800">
          <WarningOutlined className="text-xl text-amber-500 mt-0.5 shrink-0" />
          <div className="text-sm leading-relaxed">
            <p className="font-semibold text-amber-900 mb-1">
              Your email is not yet verified.
            </p>
            <p className="text-amber-800 mb-2">
              {userEmail ? (
                <>
                  The Intuit account for{" "}
                  <span className="font-semibold text-gray-900">{userEmail}</span>{" "}
                  has an unverified email address.
                </>
              ) : (
                "Your Intuit account email address has not been verified."
              )}
            </p>
            <p className="text-xs text-amber-700">
              Intuit requires your email address to be verified before your account
              can access connected applications.
            </p>
          </div>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-xs text-gray-600 leading-relaxed space-y-2">
          <p className="font-medium text-gray-800 text-sm">How to verify:</p>
          <ol className="list-decimal list-inside space-y-1.5 pl-1">
            <li>
              Click the button below to go to your Intuit{" "}
              <strong>“Sign in &amp; security”</strong> page.
            </li>
            <li>
              Next to your email address, check if the address is verified or if
              the verification email needs to be resent.
            </li>
            <li>
              Follow the instructions in the email sent by Intuit to verify.
            </li>
            <li>
              Once verified, return here and sign in with your Intuit credentials.
            </li>
          </ol>
        </div>

        <div className="space-y-3 pt-2">
          <a
            href={INTUIT_SECURITY_URL}
            target="_blank"
            rel="noopener noreferrer"
            id="verify_intuit_email_btn"
            className="w-full flex items-center justify-center gap-2 bg-[#007BFF] hover:bg-blue-600 text-white font-medium text-sm py-3 px-4 rounded-xl transition-colors shadow-sm outline-none"
          >
            <span>Verify your email in Intuit</span>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>

          <button
            type="button"
            onClick={handleLogout}
            id="intuit_unverified_logout_btn"
            className="w-full flex items-center justify-center gap-2 bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 font-medium text-sm py-2.5 px-4 rounded-xl transition-colors outline-none"
          >
            Sign out &amp; return to login
          </button>
        </div>
      </div>
    </AuthPageShell>
  );
}
